/**
 * destinoStore — Tags de destino para referências no escopo do projeto/Mesa.
 *
 * Store autocontido. Nenhum import de outros stores.
 * Persistência: campo `destinoData` dentro de project.json no Drive.
 * Segue padrão de Sumário Comentado (DriveSync READ-MERGE-WRITE).
 */

import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { bigwaToast } from '../utils/bigwaToast';
import { logger } from '../utils/logger';
import { auditLog } from '../utils/auditLog';
import type { Destino, DestinoData, ParkedStructure } from '../types/destino';
import {
  readDestinoData,
  debouncedSaveDestinos,
  immediateSaveDestinos,
  cancelPendingDestinoSaves,
} from '../services/destinoDriveSync';
import { SUMARIO_MODELS, gerarPartes, isCatalogModel } from '../constants/sumarioModels';
import type { SumarioModelKey, SumarioModelCatalogKey } from '../constants/sumarioModels';

// ---------- Constants ----------
const MAX_DESTINOS = 30;
const MAX_NAME_LENGTH = 50;

// ---------- Interface ----------
export interface DestinoState {
  // Data
  destinos: Destino[];
  assignments: Record<string, string[]>;
  activeModel?: SumarioModelKey; // ausente = legado "livre"
  parkedModels: Partial<Record<SumarioModelKey, ParkedStructure>>; // memória por modelo

  // Loading state
  isLoaded: boolean;
  loadedAt: number | null;
  projectFolderId: string | null;

  // Actions — lifecycle
  loadFromDrive: (projectFolderId: string) => Promise<void>;
  revalidateIfStale: () => Promise<void>; // Cross-device sync: background fetch if TTL expired
  reset: () => void;

  // Actions — destinos
  addDestino: (name: string) => string;
  removeDestino: (id: string) => void;
  renameDestino: (id: string, name: string) => void;
  reorderDestinos: (ids: string[]) => void;

  // Actions — modelos de sumário (Sprint 034)
  seedModel: (key: SumarioModelCatalogKey, count?: number) => boolean;
  switchModel: (key: SumarioModelKey, count?: number) => boolean;
  addModelItem: () => string;

  // Actions — assignments
  assignDestino: (refId: string, destinoId: string) => void;
  unassignDestino: (refId: string, destinoId: string) => void;
  getAssignments: (refId: string) => string[];

  // Actions — save
  saveNow: () => Promise<void>;
}

// ---------- Helpers ----------
function getDestinoData(state: DestinoState): DestinoData {
  const data: DestinoData = {
    destinos: state.destinos,
    assignments: state.assignments,
  };
  // Compat (AC-17): só inclui os campos novos quando significam algo — uma Mesa
  // livre pura persiste o mesmo shape de antes ({destinos, assignments}).
  if (state.activeModel !== undefined) data.activeModel = state.activeModel;
  if (Object.keys(state.parkedModels).length > 0) data.parkedModels = state.parkedModels;
  return data;
}

function isDuplicateName(destinos: Destino[], name: string, excludeId?: string): boolean {
  const normalized = name.trim().toLowerCase();
  return destinos.some(
    (d) => d.name.toLowerCase() === normalized && d.id !== excludeId
  );
}

/**
 * Constrói o esqueleto ordenado de um modelo (destinos novos + assignments
 * vazias), respeitando MAX_DESTINOS. Devolve { error } se o esqueleto estoura
 * o teto — recusa, sem criar parcial (AC-5). Função pura.
 */
function buildSkeleton(
  key: SumarioModelCatalogKey,
  count?: number,
): { destinos: Destino[]; assignments: Record<string, string[]> } | { error: string } {
  const partes = gerarPartes(key, count);
  if (partes.length > MAX_DESTINOS) {
    return {
      error: `O modelo gera ${partes.length} partes — acima do limite de ${MAX_DESTINOS}. Reduza a contagem.`,
    };
  }
  const destinos: Destino[] = partes.map((name) => ({
    id: nanoid(8),
    name: name.slice(0, MAX_NAME_LENGTH),
    createdAt: Date.now(),
  }));
  return { destinos, assignments: {} };
}

function scheduleSave(get: () => DestinoState): void {
  const { projectFolderId } = get();
  if (!projectFolderId) return;
  debouncedSaveDestinos(projectFolderId, () => getDestinoData(get()), (error) => {
    bigwaToast.notifyError('Não foi possível salvar destinos. Recarregando do Drive...');
    logger.error('[destinoStore.scheduleSave]', error);
    auditLog({
      operation: 'update',
      target: 'destino',
      targetId: projectFolderId,
      detail: 'Falha ao salvar destinos no Drive — forceReload',
      success: false,
      error: error instanceof Error ? error.message : String(error),
    });
    // forceReload: re-read from Drive to restore consistent state
    readDestinoData(projectFolderId).then((data) => {
      useDestinoStore.setState({
        destinos: data?.destinos ?? [],
        assignments: data?.assignments ?? {},
        activeModel: data?.activeModel,
        parkedModels: data?.parkedModels ?? {},
      });
    }).catch((reloadError) => {
      logger.error('[destinoStore.forceReload]', reloadError);
    });
  });
}

// Cross-device sync: tracks last local mutation to prevent stale revalidation from overwriting in-flight changes
let _lastMutationAt = 0;

// ---------- Store ----------
export const useDestinoStore = create<DestinoState>((set, get) => ({
  // Initial state
  destinos: [],
  assignments: {},
  activeModel: undefined,
  parkedModels: {},
  isLoaded: false,
  loadedAt: null,
  projectFolderId: null,

  // ---------- Lifecycle ----------

  loadFromDrive: async (projectFolderId: string) => {
    // TTL: skip reload só quando for a MESMA Mesa e os dados estiverem frescos
    // (< 5 min). Trocar de Mesa sempre recarrega — senão o Horizonte da nova
    // Mesa exibiria os destinos da anterior (Sprint 036).
    const mesmaMesa = get().projectFolderId === projectFolderId;
    const loadedAt = get().loadedAt;
    if (mesmaMesa && loadedAt && Date.now() - loadedAt < 300_000) return;

    try {
      const fetchStartedAt = Date.now();
      const data = await readDestinoData(projectFolderId);

      // Cross-device sync guard: if a local mutation happened while we were
      // fetching, discard the (now stale) server data to avoid overwriting
      // the optimistic state.
      if (_lastMutationAt > fetchStartedAt) return;

      set({
        destinos: data?.destinos ?? [],
        assignments: data?.assignments ?? {},
        activeModel: data?.activeModel,
        parkedModels: data?.parkedModels ?? {},
        isLoaded: true,
        loadedAt: Date.now(),
        projectFolderId,
      });
    } catch (error) {
      logger.error('[destinoStore.loadFromDrive]', error);
      // Load with empty state — non-blocking
      set({
        destinos: [],
        assignments: {},
        activeModel: undefined,
        parkedModels: {},
        isLoaded: true,
        projectFolderId,
      });
    }
  },

  /**
   * Cross-device sync: background revalidation if TTL expired.
   * Does NOT block the UI.
   */
  revalidateIfStale: async () => {
    const projectFolderId = get().projectFolderId;
    if (!projectFolderId) return;

    // Only revalidate if TTL has expired
    const loadedAt = get().loadedAt;
    if (loadedAt && Date.now() - loadedAt < 300_000) return;

    try {
      const fetchStartedAt = Date.now();
      const data = await readDestinoData(projectFolderId);

      // Guard: discard if a local mutation happened during the fetch
      if (_lastMutationAt > fetchStartedAt) return;

      set({
        destinos: data?.destinos ?? [],
        assignments: data?.assignments ?? {},
        activeModel: data?.activeModel,
        parkedModels: data?.parkedModels ?? {},
        isLoaded: true,
        loadedAt: Date.now(),
        projectFolderId,
      });
    } catch (error) {
      // Background revalidation — log but don't surface to the user
      logger.error('[destinoStore.revalidateIfStale]', error);
    }
  },

  reset: () => {
    cancelPendingDestinoSaves();
    set({
      destinos: [],
      assignments: {},
      activeModel: undefined,
      parkedModels: {},
      isLoaded: false,
      loadedAt: null,
      projectFolderId: null,
    });
  },

  // ---------- Destinos ----------

  addDestino: (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) return '';

    const { destinos } = get();

    if (destinos.length >= MAX_DESTINOS) {
      bigwaToast.notifyError(`Limite de ${MAX_DESTINOS} destinos atingido.`);
      return '';
    }

    const safeName = trimmed.slice(0, MAX_NAME_LENGTH);

    if (isDuplicateName(destinos, safeName)) {
      // Return the existing destino's id
      const existing = destinos.find(
        (d) => d.name.toLowerCase() === safeName.toLowerCase()
      );
      return existing?.id ?? '';
    }

    const id = nanoid(8);
    const newDestino: Destino = {
      id,
      name: safeName,
      createdAt: Date.now(),
    };

    set({ destinos: [...destinos, newDestino] });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'create',
      target: 'destino',
      targetId: id,
      detail: `Criou destino '${safeName}'`,
      success: true,
    });

    return id;
  },

  removeDestino: (id: string) => {
    const { destinos, assignments } = get();
    const destino = destinos.find((d) => d.id === id);
    if (!destino) return;

    // Remove from destinos list
    const newDestinos = destinos.filter((d) => d.id !== id);

    // Remove from all assignments
    const newAssignments: Record<string, string[]> = {};
    for (const [refId, ids] of Object.entries(assignments)) {
      const filtered = ids.filter((did) => did !== id);
      if (filtered.length > 0) {
        newAssignments[refId] = filtered;
      }
    }

    set({ destinos: newDestinos, assignments: newAssignments });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'delete',
      target: 'destino',
      targetId: id,
      detail: `Removeu destino '${destino.name}'`,
      success: true,
    });
  },

  renameDestino: (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const { destinos } = get();
    const safeName = trimmed.slice(0, MAX_NAME_LENGTH);

    if (isDuplicateName(destinos, safeName, id)) {
      bigwaToast.notifyError('Já existe um destino com esse nome.');
      return;
    }

    const newDestinos = destinos.map((d) =>
      d.id === id ? { ...d, name: safeName } : d
    );

    set({ destinos: newDestinos });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'update',
      target: 'destino',
      targetId: id,
      detail: `Renomeou destino para '${safeName}'`,
      success: true,
    });
  },

  reorderDestinos: (ids: string[]) => {
    const { destinos } = get();
    const byId = new Map(destinos.map((d) => [d.id, d]));
    const reordered = ids.map((id) => byId.get(id)).filter(Boolean) as Destino[];

    // Append any destinos not in the ids array (safety)
    for (const d of destinos) {
      if (!ids.includes(d.id)) {
        reordered.push(d);
      }
    }

    set({ destinos: reordered });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'update',
      target: 'destino',
      targetId: ids.join(','),
      detail: `Reordenou ${ids.length} destinos`,
      success: true,
    });
  },

  // ---------- Modelos de sumário (Sprint 034) ----------

  seedModel: (key, count) => {
    const built = buildSkeleton(key, count);
    if ('error' in built) {
      bigwaToast.notifyError(built.error);
      return false;
    }
    set({ destinos: built.destinos, assignments: built.assignments, activeModel: key });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'create',
      target: 'destino',
      targetId: key,
      detail: `Semeou modelo '${key}' (${built.destinos.length} partes)`,
      success: true,
    });
    return true;
  },

  switchModel: (key, count) => {
    const { destinos, assignments, activeModel, parkedModels } = get();
    const fromKey: SumarioModelKey = activeModel ?? 'livre';
    if (fromKey === key) return false; // já é o modelo ativo — no-op

    // Decide a estrutura-alvo ANTES de mutar (atômico): restaura do parque,
    // vazia p/ 'livre' sem memória, ou semeia limpa. Teto checado aqui (aborta).
    let nextDestinos: Destino[];
    let nextAssignments: Record<string, string[]>;
    const parkedTarget = parkedModels[key];
    if (parkedTarget) {
      nextDestinos = parkedTarget.destinos;
      nextAssignments = parkedTarget.assignments;
    } else if (!isCatalogModel(key)) {
      // 'livre' sem memória parqueada → estrutura vazia
      nextDestinos = [];
      nextAssignments = {};
    } else {
      const built = buildSkeleton(key, count);
      if ('error' in built) {
        bigwaToast.notifyError(built.error);
        return false; // aborta sem parquear nem mutar
      }
      nextDestinos = built.destinos;
      nextAssignments = built.assignments;
    }

    // Parqueia a ativa atual sob fromKey (append-only: nunca destrói); o alvo
    // restaurado sai do parque (vira ativo).
    const nextParked: Partial<Record<SumarioModelKey, ParkedStructure>> = {
      ...parkedModels,
      [fromKey]: { destinos, assignments },
    };
    delete nextParked[key];

    set({
      destinos: nextDestinos,
      assignments: nextAssignments,
      activeModel: isCatalogModel(key) ? key : undefined,
      parkedModels: nextParked,
    });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'update',
      target: 'destino',
      targetId: key,
      detail: `Trocou estrutura: '${fromKey}' parqueada, ativou '${key}'`,
      success: true,
    });
    return true;
  },

  addModelItem: () => {
    const { destinos, activeModel } = get();
    if (!activeModel || !isCatalogModel(activeModel)) return ''; // livre/none → addDestino livre
    const model = SUMARIO_MODELS[activeModel];
    if (model.kind !== 'repeatable' || !model.repeatable) return ''; // projeto (fixo) não inclui item

    if (destinos.length >= MAX_DESTINOS) {
      bigwaToast.notifyError(`Limite de ${MAX_DESTINOS} destinos atingido.`);
      return '';
    }

    const inicioSet = new Set(model.bookendsInicio);
    const fimSet = new Set(model.bookendsFim);
    // conta o miolo (nem início nem fim) — robusto a rename dos itens do miolo
    const mioloCount = destinos.filter(
      (d) => !inicioSet.has(d.name) && !fimSet.has(d.name),
    ).length;
    const nextName = model.repeatable.gerarNome(mioloCount + 1);

    if (isDuplicateName(destinos, nextName)) {
      bigwaToast.notifyError(`Já existe "${nextName}".`);
      return '';
    }

    // insere antes do 1º bookend de fim (ex.: Conclusão); se ausente, no fim
    let insertIdx = destinos.findIndex((d) => fimSet.has(d.name));
    if (insertIdx === -1) insertIdx = destinos.length;

    const novo: Destino = {
      id: nanoid(8),
      name: nextName.slice(0, MAX_NAME_LENGTH),
      createdAt: Date.now(),
    };
    const next = [...destinos.slice(0, insertIdx), novo, ...destinos.slice(insertIdx)];

    set({ destinos: next });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'create',
      target: 'destino',
      targetId: novo.id,
      detail: `Incluiu '${nextName}' (modelo '${activeModel}')`,
      success: true,
    });
    return novo.id;
  },

  // ---------- Assignments ----------

  assignDestino: (refId: string, destinoId: string) => {
    const { assignments } = get();
    const current = assignments[refId] ?? [];

    if (current.includes(destinoId)) return;

    set({
      assignments: {
        ...assignments,
        [refId]: [...current, destinoId],
      },
    });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'update',
      target: 'destino-assignment',
      targetId: refId,
      detail: `Atribuiu destino '${destinoId}' à referência '${refId}'`,
      success: true,
    });
  },

  unassignDestino: (refId: string, destinoId: string) => {
    const { assignments } = get();
    const current = assignments[refId];
    if (!current) return;

    const filtered = current.filter((id) => id !== destinoId);
    const newAssignments = { ...assignments };

    if (filtered.length === 0) {
      delete newAssignments[refId];
    } else {
      newAssignments[refId] = filtered;
    }

    set({ assignments: newAssignments });
    _lastMutationAt = Date.now();
    scheduleSave(get);

    auditLog({
      operation: 'update',
      target: 'destino-assignment',
      targetId: refId,
      detail: `Removeu destino '${destinoId}' da referência '${refId}'`,
      success: true,
    });
  },

  getAssignments: (refId: string): string[] => {
    return get().assignments[refId] ?? [];
  },

  // ---------- Save ----------

  saveNow: async () => {
    const { projectFolderId } = get();
    if (!projectFolderId) return;
    await immediateSaveDestinos(projectFolderId, () => getDestinoData(get()));
  },
}));
